import React, { useCallback, useEffect, useRef, useState } from 'react'
import { DatePicker } from 'antd'
import type { CustomCellEditorProps } from 'ag-grid-react'
import { useGridCellEditor } from 'ag-grid-react'
import dayjs from 'dayjs'
import { parseDate, formatAsDate } from '../../../../../config/dayjsConfig'

/**
 * Date Cell Editor wrapping Ant Design DatePicker
 *
 * The popup is rendered in a container that prevents mousedown default,
 * so clicking the calendar doesn't steal focus from the AG Grid cell.
 */
const DateCellEditor = ({ value, onValueChange, stopEditing, cellStartedEdit }: CustomCellEditorProps<any, string>) => {
  const pickerRef = useRef<any>(null)
  const popupContainerRef = useRef<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(!!cellStartedEdit)
  const [localValue, setLocalValue] = useState<dayjs.Dayjs | null>(value ? parseDate(value) || dayjs(value) : null)

  // Create a popup container that prevents mousedown from stealing focus
  const getPopupContainer = useCallback(() => {
    if (!popupContainerRef.current) {
      popupContainerRef.current = document.createElement('div')
      popupContainerRef.current.addEventListener('mousedown', e => {
        e.preventDefault()
      })
      document.body.appendChild(popupContainerRef.current)
    }
    return popupContainerRef.current
  }, [])

  // Cleanup popup container on unmount
  useEffect(() => {
    return () => {
      if (popupContainerRef.current) {
        document.body.removeChild(popupContainerRef.current)
        popupContainerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (cellStartedEdit) {
      setTimeout(() => pickerRef.current?.focus(), 0)
    }
  }, [cellStartedEdit])

  const focusIn = useCallback(() => {
    setIsOpen(true)
    setTimeout(() => pickerRef.current?.focus(), 0)
  }, [])

  const focusOut = useCallback(() => {
    setIsOpen(false)
  }, [])

  useGridCellEditor({ focusIn, focusOut })

  const handleChange = (date: dayjs.Dayjs | null) => {
    setLocalValue(date)
    const formatted = date ? formatAsDate(date) || '' : ''
    onValueChange(formatted)
  }

  return (
    <DatePicker
      ref={pickerRef}
      value={localValue}
      onChange={handleChange}
      format="DD.MM.YYYY"
      open={isOpen}
      onOpenChange={open => {
        setIsOpen(open)
        if (!open && cellStartedEdit) {
          stopEditing()
        }
      }}
      style={{ width: '100%' }}
      getPopupContainer={getPopupContainer}
    />
  )
}

DateCellEditor.displayName = 'DateCellEditor'

export default DateCellEditor
